const normTxt = (v) => (v ?? "").toString().trim().toLowerCase();
const unwrapExperian = (maybeDoc) => maybeDoc?.response ?? maybeDoc ?? null;

export const applyMortalityUserConfirmedNotClientLocal = (
  experianDocOrResponse,
  { confirmedBy = null } = {}
) => {
  const isWrapped = !!experianDocOrResponse?.response;
  const base = unwrapExperian(experianDocOrResponse);
  if (!base?.clientResponsePayload) return experianDocOrResponse;

  const nextInner = structuredClone(base);

  const existing = nextInner.clientResponsePayload.overrides || {};
  nextInner.clientResponsePayload.overrides = {
    ...existing,
    mortality: {
      ...(existing.mortality || {}),
      userConfirmedNotClient: true,
      confirmedBy,
      confirmedAt: new Date().toISOString(),
    },
  };

  return isWrapped
    ? { ...experianDocOrResponse, response: nextInner }
    : nextInner;
};



export const getLatestMortalityLog = (experianDocOrResponse) => {
  const r = unwrapExperian(experianDocOrResponse);
  if (!r) return null;

  const decisionElements = r?.clientResponsePayload?.decisionElements || [];

  // flatten ALL audit logs across ALL decision elements
  const allLogs = decisionElements.flatMap((el) =>
    Array.isArray(el?.auditLogs) ? el.auditLogs : []
  );

  // tolerant match for vendor variations
  const mortalityLogs = allLogs.filter((l) => {
    const t = normTxt(l?.eventType);
    return t.includes("mortality") && t.includes("halo") && t.includes("1");
  });

  if (!mortalityLogs.length) return null;

  return [...mortalityLogs].sort((a, b) => {
    const tA = a?.eventDate ? new Date(a.eventDate).getTime() : 0;
    const tB = b?.eventDate ? new Date(b.eventDate).getTime() : 0;
    return tB - tA;
  })[0];
};


/**
 * Returns a richer decision:
 * - pass (no match)
 * - needs_user_confirmation (match found but no override)
 * - pass_overridden (match found + user confirmed NOT client)
 * - unknown
 */
export const getMortalityDecision = (experianDocOrResponse) => {
  const r = experianDocOrResponse;
  if (!r) return null;


  const latest = getLatestMortalityLog(r);
  if (!latest) return null;

  const rawOutcome = (latest?.eventOutcome ?? "").toString().trim();
  const o = normTxt(rawOutcome);

  const override = r?.clientResponsePayload?.overrides?.mortality || null;

  // console.log("Mortality override:", override);
  const userConfirmedNotClient = override?.userConfirmedNotClient === true;

  const isNoMatch =
    o.includes("no match") ||
    o.includes("not matched") ||
    o.includes("no mortality match");

  const isMatch =
    !isNoMatch &&
    (o.includes("match found") ||
      o.includes("matched") ||
      o.includes("potential match"));

  if (isMatch && userConfirmedNotClient) {
    return {
      status: "pass_overridden",
      label: "Pass",
      reason: "Mortality match overridden.",
      rawOutcome,
      override,
    };
  }

  if (isMatch) {
    return {
      status: "needs_user_confirmation",
      label: "Fail",
      reason: "Mortality match found, please confirm it's NOT the client.",
      rawOutcome,
      override,
    };
  }

  if (isNoMatch) {
    return {
      status: "pass",
      label: "Pass",
      reason: "No mortality match found.",
      rawOutcome,
      override,
    };
  }

  return {
    status: "unknown",
    label: "Unknown",
    reason: "Unexpected mortality outcome",
    rawOutcome,
    override,
  };
};
